class GiftFinderFilters extends HTMLElement {
  connectedCallback() {
    this.priceBreak1 = parseInt(this.dataset.priceBreak1, 10);
    this.priceBreak2 = parseInt(this.dataset.priceBreak2, 10);
    // No pagination — the grid always shows at most this many matches.
    // `max_products` (the section setting controlling how many products get
    // loaded into the DOM at all) is the real page-load-speed lever; this is
    // just how many of those loaded/matching products are ever visible.
    // Driven by the "Products to show" section setting (data-visible-limit);
    // 8 is only a fallback in case the attribute is ever missing.
    this.visibleLimit = parseInt(this.dataset.visibleLimit, 10) || 8;
    // Per-card caches, keyed by the `.product-card` element so they survive
    // repeated filter clicks without re-parsing/re-reading the DOM each time.
    this.cardVariants = new WeakMap(); // parsed data-variants
    this.cardDefaults = new WeakMap(); // original price/compare/url/img/anchors, for restoring on "All"
    this.init();
  }

  init() {
    this.pills = Array.from(this.querySelectorAll('.gift-finder__pill'));
    this.items = Array.from(this.querySelectorAll('[data-grid] li.grid__item'));
    this.countEl = this.querySelector('[data-count]');
    this.emptyState = this.querySelector('[data-empty-state]');

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => this.selectPill(pill));
    });

    this.applyFilters();
  }

  selectPill(pill) {
    if (pill.classList.contains('is-active')) return;

    const group = pill.dataset.filterGroup;
    this.pills
      .filter((candidate) => candidate.dataset.filterGroup === group)
      .forEach((candidate) => {
        const isSelected = candidate === pill;
        candidate.classList.toggle('is-active', isSelected);
        candidate.setAttribute('aria-pressed', String(isSelected));
      });
    this.applyFilters();
  }

  activeValue(group) {
    const active = this.pills.find(
      (pill) => pill.dataset.filterGroup === group && pill.classList.contains('is-active')
    );
    return active ? active.dataset.filterValue : 'all';
  }

  priceBucket(priceCents) {
    if (priceCents <= this.priceBreak1) return '0';
    if (priceCents <= this.priceBreak2) return '1';
    return '2';
  }

  formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.shopSettings) {
      return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
    }
    return `₹${Math.round(cents / 100).toLocaleString('en-IN')}`;
  }

  // A product can have several variants at different price points (e.g.
  // different pot sizes) — it counts as a match for a price pill if *any*
  // of its variants falls in that bucket, not just whichever variant
  // happens to be the one shown on the card (product-card.liquid's
  // display_variant, picked by featured/fallback-variant logic unrelated
  // to price).
  //
  // But it must actually be buyable at that price to count: if the only
  // variant(s) in this bucket are sold out while the product has an
  // available variant in a *different* bucket, matching here would make
  // applyPriceVariant show that sold-out variant — a customer picks
  // "Under ₹499" and sees "Sold Out" on a product that's really in stock,
  // just at a higher price. So require an available variant in-bucket,
  // unless the product has no available variant anywhere — a genuinely
  // sold-out-everywhere product should still surface (correctly labelled
  // Sold Out) rather than silently disappear from every price filter.
  productMatchesPriceBucket(card, bucket) {
    if (!card) return false;
    const variants = this.getCardVariants(card);
    const inBucket = variants.filter((variant) => this.priceBucket(variant.price) === bucket);
    if (!inBucket.length) return false;
    if (inBucket.some((variant) => variant.available)) return true;
    return !variants.some((variant) => variant.available);
  }

  // Reads `data-variants` — built in product-card.liquid from plain
  // `{{ variant.price }}` interpolation (cents), deliberately NOT from
  // `product.variants | json` (which serializes price as a decimal, e.g.
  // 299.0). Matching and display both read this one source so they can
  // never disagree with each other the way they briefly did when matching
  // used raw cents and the display swap used the decimal-serialized JSON.
  getCardVariants(card) {
    if (this.cardVariants.has(card)) return this.cardVariants.get(card);
    let variants = [];
    if (card.dataset.variants) {
      try {
        variants = JSON.parse(card.dataset.variants);
      } catch (error) {
        variants = [];
      }
    }
    this.cardVariants.set(card, variants);
    return variants;
  }

  // Captures the card's as-rendered state once, the first time it's touched,
  // so a later switch back to "All" (or to a bucket this card no longer
  // matches) can put it back exactly as product-card.liquid originally
  // rendered it — not just to *some* variant's price.
  snapshotDefault(card) {
    if (this.cardDefaults.has(card)) return this.cardDefaults.get(card);

    const priceEl = card.querySelector('.product-card__price');
    const compareEl = card.querySelector('.product-card__compare-price');
    const img = card.querySelector('.product-card__img');
    const anchors = Array.from(card.querySelectorAll('a')).map((anchor) => ({
      el: anchor,
      href: anchor.getAttribute('href') || '',
    }));

    const snapshot = {
      priceText: priceEl ? priceEl.textContent : '',
      compareText: compareEl ? compareEl.textContent : '',
      compareDisplay: compareEl ? compareEl.style.display : '',
      productUrl: card.dataset.productUrl,
      imgSrc: img ? img.src : null,
      imgSrcset: img ? img.getAttribute('srcset') : null,
      anchors,
    };
    this.cardDefaults.set(card, snapshot);
    return snapshot;
  }

  restoreDefaultVariant(card) {
    const snapshot = this.cardDefaults.get(card);
    if (!snapshot) return; // never touched — already showing its default

    const priceEl = card.querySelector('.product-card__price');
    if (priceEl) priceEl.textContent = snapshot.priceText;

    const compareEl = card.querySelector('.product-card__compare-price');
    if (compareEl) {
      compareEl.textContent = snapshot.compareText;
      compareEl.style.display = snapshot.compareDisplay;
    }

    const img = card.querySelector('.product-card__img');
    if (img && snapshot.imgSrc) {
      if (snapshot.imgSrcset) img.setAttribute('srcset', snapshot.imgSrcset);
      img.src = snapshot.imgSrc;
    }

    card.dataset.productUrl = snapshot.productUrl;
    snapshot.anchors.forEach(({ el, href }) => el.setAttribute('href', href));
  }

  // Which variant to show once a card has more than one that qualifies for
  // the selected bucket:
  //   1. The merchant's manually-chosen featured variant (product-card.liquid's
  //      `fv`, from custom.featured_product) — if it happens to fall in this
  //      bucket and is in stock, that pick should win everywhere on the site,
  //      gift-finder included, not get silently overridden.
  //   2. Otherwise, the highest-priced in-stock variant within the bucket —
  //      not the first one found (usually the smallest/cheapest size given
  //      variants are typically listed small-to-large), so "Under ₹499"
  //      shows the ₹299 variant instead of ₹249 when a product has both.
  //   3. If nothing in the bucket is in stock, fall back to the
  //      highest-priced one regardless of availability rather than showing
  //      nothing. productMatchesPriceBucket only lets a card reach this
  //      bucket sold-out-in-bucket when the product has no available
  //      variant anywhere, so this path never hides a variant that's
  //      actually buyable elsewhere.
  pickVariantForBucket(card, inBucket) {
    const fvId = card.dataset.fvId ? parseInt(card.dataset.fvId, 10) : null;
    if (fvId) {
      const featured = inBucket.find((candidate) => candidate.id === fvId && candidate.available);
      if (featured) return featured;
    }

    const available = inBucket.filter((candidate) => candidate.available);
    const pool = available.length ? available : inBucket;
    return pool.reduce((highest, candidate) => (candidate.price > highest.price ? candidate : highest), pool[0]);
  }

  // Swaps the card's displayed price/image/link to whichever of its
  // variants actually falls in the selected price bucket — a card matched
  // "₹499 – ₹999" should show a ₹499–₹999 variant, not silently keep
  // showing its unrelated default-display variant's price.
  applyPriceVariant(card, bucket) {
    const snapshot = this.snapshotDefault(card);
    const variants = this.getCardVariants(card);
    const inBucket = variants.filter((variant) => this.priceBucket(variant.price) === bucket);
    if (!inBucket.length) return; // shouldn't happen if this card matched the bucket, but don't break the card if it does

    const variant = this.pickVariantForBucket(card, inBucket);

    const priceEl = card.querySelector('.product-card__price');
    if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

    const compareEl = card.querySelector('.product-card__compare-price');
    if (compareEl) {
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareEl.textContent = hasDiscount ? this.formatMoney(variant.compare_at_price) : '';
      compareEl.style.display = hasDiscount ? '' : 'none';
    }

    const img = card.querySelector('.product-card__img');
    if (img && variant.image) {
      // Drop the responsive srcset, otherwise the browser keeps showing the srcset image.
      img.removeAttribute('srcset');
      img.src = variant.image.replace(/(\.[a-z]+)(\?.*)?$/i, (match, ext, qs) =>
        ext + (qs ? `${qs}&width=600` : '?width=600'));
    }

    const baseUrl = card.dataset.baseUrl || (snapshot.productUrl || '').split('?')[0];
    const variantUrl = `${baseUrl}?variant=${variant.id}`;
    card.dataset.productUrl = variantUrl;
    snapshot.anchors.forEach(({ el, href }) => {
      if (href === baseUrl || href.startsWith(`${baseUrl}?`)) el.href = variantUrl;
    });
  }

  // A fully sold-out product (no available variant anywhere) is the only
  // case productMatchesPriceBucket lets through without an in-bucket
  // available variant (see its comment) — so this is also the right check
  // outside price filtering: "does this card have any variant a customer
  // could actually buy right now?"
  isCardAvailable(card) {
    if (!card) return true;
    const variants = this.getCardVariants(card);
    if (!variants.length) return true; // no variant data to judge by — don't demote
    return variants.some((variant) => variant.available);
  }

  applyFilters() {
    const activePurpose = this.activeValue('purpose');
    const activePrice = this.activeValue('price');

    const matches = this.items.filter((item) => {
      const card = item.querySelector('.product-card');
      const purposes = card && card.dataset.giftPurpose ? card.dataset.giftPurpose.split(',') : [];

      const purposeMatch = activePurpose === 'all' || purposes.includes(activePurpose);
      const priceMatch = activePrice === 'all' || this.productMatchesPriceBucket(card, activePrice);

      if (card) {
        if (activePrice === 'all') {
          this.restoreDefaultVariant(card);
        } else if (priceMatch) {
          this.applyPriceVariant(card, activePrice);
        }
      }

      return purposeMatch && priceMatch;
    });

    // In-stock matches fill the visible slots first — a sold-out product
    // sitting earlier in the grid's DOM order shouldn't bump an in-stock
    // match out of the first `visibleLimit` cards. Array#sort is stable, so
    // this only reorders across the available/sold-out boundary and leaves
    // relative order within each group untouched.
    matches.sort((a, b) => {
      const aAvailable = this.isCardAvailable(a.querySelector('.product-card'));
      const bAvailable = this.isCardAvailable(b.querySelector('.product-card'));
      return Number(bAvailable) - Number(aAvailable);
    });

    this.renderPage(matches);
  }

  renderPage(matches) {
    const visible = new Set(matches.slice(0, this.visibleLimit));

    this.items.forEach((item) => {
      item.hidden = !visible.has(item);
    });

    if (this.countEl) {
      this.countEl.textContent = `${matches.length} gift${matches.length === 1 ? '' : 's'}`;
    }

    if (this.emptyState) {
      this.emptyState.hidden = matches.length > 0;
    }
  }
}

if (!customElements.get('gift-finder-filters')) {
  customElements.define('gift-finder-filters', GiftFinderFilters);
}
